import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import planoService from '../services/planoTrabalhoService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import WorkspaceOverview, { type WorkspaceMetric } from './dashboard/WorkspaceOverview';
import WorkspaceSection from './dashboard/WorkspaceSection';
import WorkspaceSectionGroup from './dashboard/WorkspaceSectionGroup';

const formatDate = (value?: string) => {
  if (!value) return 'Nao informado';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('pt-BR').format(date);
};

const planoStatusLabel = (status?: string) => {
  switch (status) {
    case 'CONCLUIDO':
      return 'Concluido';
    case 'REPROVADO':
      return 'Reprovado';
    case 'DEVOLVIDO_CORRECAO':
      return 'Devolvido';
    case 'PENDENTE_RETIFICACAO':
      return 'Pendente Retificacao';
    case 'APROVADO':
      return 'Aprovado';
    case 'EM_ANALISE':
      return 'Em analise';
    default:
      return 'Rascunho';
  }
};

const planoStatusTone = (status?: string) => {
  switch (status) {
    case 'CONCLUIDO':
    case 'APROVADO':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case 'REPROVADO':
    case 'DEVOLVIDO_CORRECAO':
      return 'border-red-200 bg-red-50 text-red-700';
    case 'PENDENTE_RETIFICACAO':
      return 'border-orange-200 bg-orange-50 text-orange-700';
    case 'EM_ANALISE':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    default:
      return 'border-slate-200 bg-slate-50 text-slate-700';
  }
};

const modalidadeLabel = (modalidade?: string) => {
  switch (modalidade) {
    case 'TERMO_COLABORACAO':
      return 'Termo de colaboracao';
    case 'TERMO_FOMENTO':
      return 'Termo de fomento';
    default:
      return modalidade || 'Nao informada';
  }
};

type ProcuradoriaWorkspaceProps = {
  auxiliary?: boolean;
};

const ProcuradoriaWorkspace: React.FC<ProcuradoriaWorkspaceProps> = ({ auxiliary = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [planos, setPlanos] = useState<PlanoTrabalho[]>([]);
  const [collapsedBoxes, setCollapsedBoxes] = useState<Set<string>>(() => new Set([
    'como-funciona',
    'atalhos',
  ]));

  const toggleBox = (id: string) => setCollapsedBoxes(prev => {
    const next = new Set(prev);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    return next;
  });
  const isBoxCollapsed = (id: string) => collapsedBoxes.has(id);
  const renderCollapseButton = (id: string) => (
    <button
      type="button"
      onClick={() => toggleBox(id)}
      className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
    >
      {isBoxCollapsed(id) ? 'Expandir' : 'Recolher'}
    </button>
  );
  const fixedCardClass = 'flex h-fit min-h-0 flex-col overflow-hidden rounded-lg border shadow-sm';
  const fixedCardBodyClass = 'min-h-0 flex-1 overflow-y-auto';

  const openAnaliseJuridica = (planoId: string) => {
    navigate(`/dashboard/plano/${planoId}/analise-juridica`);
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await planoService.listAll().catch(() => []);
        if (!active) return;
        setPlanos(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!active) return;
        setError(err?.response?.data?.error || 'Nao foi possivel carregar a area da Procuradoria Geral Municipal.');
        setPlanos([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const planosJuridico = useMemo(
    () => planos.filter((item) => item.etapaAtual === 'PROCURADORIA_JURIDICA' && ['EM_ANALISE', 'APROVADO', 'PENDENTE_RETIFICACAO'].includes(item.status || '')),
    [planos]
  );

  const planosAguardandoPrimeiraAnalise = useMemo(
    () => planosJuridico.filter((item) => !(item.parecerJuridico && item.parecerJuridico.trim())),
    [planosJuridico]
  );

  const planoPrioritario = planosAguardandoPrimeiraAnalise[0] || planosJuridico[0] || null;

  const planosAnaliseIniciada = useMemo(
    () => planosJuridico.filter((item) => Boolean(item.parecerJuridico && item.parecerJuridico.trim())),
    [planosJuridico]
  );

  const stats = useMemo(() => {
    const concluidos = planos.filter((item) => item.etapaAtual === 'CONVENIOS_FORMALIZACAO' || item.status === 'CONCLUIDO').length;
    const reprovados = planos.filter((item) => item.etapaAtual === 'PROCURADORIA_JURIDICA' && item.status === 'REPROVADO').length;
    const comParecer = planos.filter((item) => Boolean(item.parecerJuridico && item.parecerJuridico.trim())).length;
    return {
      naFila: planosJuridico.length,
      concluidos,
      reprovados,
      comParecer,
    };
  }, [planos, planosJuridico]);

  const cards: WorkspaceMetric[] = [
    { label: auxiliary ? 'Na fila para análise' : 'Na fila jurídica', value: stats.naFila, tone: 'neutral' },
    { label: auxiliary ? 'Pareceres registrados' : 'Com parecer registrado', value: stats.comParecer, tone: 'progress' },
    { label: 'Encaminhados adiante', value: stats.concluidos, tone: 'success' },
    { label: 'Reprovados', value: stats.reprovados, tone: 'critical' },
  ];

  const quickLinks = [
    {
      label: 'Fila PGM completa',
      description: 'Abrir a lista filtrada dos planos aguardando decisão da Procuradoria Geral Municipal.',
      onClick: () => navigate('/dashboard/gestao/planos-trabalho?etapaAtual=PROCURADORIA_JURIDICA'),
    },
    {
      label: 'Prestacao de contas',
      description: 'Acessar a visao consolidada dos planos e seus desdobramentos.',
      onClick: () => navigate('/dashboard/gestao/prestacao-contas-planos'),
    },
  ];

  return (
    <div className="w-full space-y-6 dark:[&_h1]:text-white dark:[&_h2]:text-white dark:[&_.bg-white]:bg-slate-950 dark:[&_.bg-white\\/90]:bg-slate-950/90 dark:[&_.bg-slate-50]:bg-slate-900 dark:[&_.border-slate-200]:border-slate-800 dark:[&_.text-slate-900]:text-slate-100 dark:[&_.text-slate-800]:text-slate-200 dark:[&_.text-slate-700]:text-slate-300 dark:[&_.text-slate-600]:text-slate-400 dark:[&_.text-slate-500]:text-slate-400 dark:[&_tr:hover]:bg-slate-900">
      <div>
        <WorkspaceOverview
          profile={auxiliary ? 'Auxiliar PGM' : 'Procuradoria Geral Municipal'}
          description={auxiliary
            ? 'Elaboração de pareceres e encaminhamento para supervisão.'
            : 'Análises jurídicas e decisões da Procuradoria.'}
          metrics={cards}
          loading={loading}
        />

        <div className={`mt-5 ${fixedCardClass} border-blue-200 bg-white/90`}>
          <div className="flex shrink-0 items-center justify-between gap-3 px-5 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-700">Acao principal</h2>
            {renderCollapseButton('acao-principal')}
          </div>
          {!isBoxCollapsed('acao-principal') && (
          <div className={`${fixedCardBodyClass} flex flex-col gap-4 px-5 pb-4 lg:flex-row lg:items-center lg:justify-between`}>
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-slate-900">
                {planoPrioritario ? 'Entrar direto na analise juridica' : 'Nenhum plano pendente na fila juridica'}
              </h2>
              <p className="max-w-2xl text-sm leading-6 text-slate-600">
                {planoPrioritario
                  ? 'Abra o plano ja no ponto de decisao da Procuradoria Geral Municipal, com destaque no bloco do workflow jurídico.'
                  : 'Quando um plano chegar para a Procuradoria Geral Municipal, o atalho principal aparecera aqui.'}
              </p>
              {planoPrioritario && (
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-medium text-slate-700">
                    Plano: {planoPrioritario.titulo || 'Nao informado'}
                  </span>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-medium text-slate-700">
                    {planoPrioritario.parecerJuridico?.trim() ? 'Analise iniciada' : 'Aguardando primeira analise'}
                  </span>
                </div>
              )}
            </div>

            <div className="flex w-full flex-col gap-3 lg:w-auto lg:min-w-[280px]">
              <button
                type="button"
                onClick={() => {
                  if (planoPrioritario) {
                    openAnaliseJuridica(planoPrioritario.id);
                    return;
                  }
                  navigate('/dashboard/gestao/planos-trabalho?etapaAtual=PROCURADORIA_JURIDICA');
                }}
                className="inline-flex items-center justify-center rounded-2xl bg-blue-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-800"
              >
                {planoPrioritario ? 'Abrir analise juridica' : 'Abrir fila juridica'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard/gestao/planos-trabalho?etapaAtual=PROCURADORIA_JURIDICA')}
                className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                Ver todos os planos da Procuradoria Geral Municipal
              </button>
            </div>
          </div>
          )}
        </div>

      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className={`${fixedCardClass} border-slate-200 bg-white`}>
          <div className="flex shrink-0 items-center justify-between gap-3 px-5 py-3">
            <h2 className="text-lg font-bold text-slate-900">Como esta etapa funciona</h2>
            {renderCollapseButton('como-funciona')}
          </div>
          {!isBoxCollapsed('como-funciona') && (
          <div className={`${fixedCardBodyClass} grid gap-3 px-5 pb-4 md:grid-cols-3`}>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">1. Receber</p>
              <p className="mt-2 text-sm leading-6 text-slate-700">O plano chega apos aprovacao da Comissao de Selecao.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">2. Emitir parecer</p>
              <p className="mt-2 text-sm leading-6 text-slate-700">O parecer juridico e o centro da decisao. O acesso deve levar direto a esse ponto.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">3. Decidir</p>
              <p className="mt-2 text-sm leading-6 text-slate-700">A decisao possivel aqui e aprovar para formalizacao ou reprovar.</p>
            </div>
          </div>
          )}
        </div>

        <div className={`${fixedCardClass} border-slate-200 bg-white`}>
          <div className="flex shrink-0 items-center justify-between gap-3 px-5 py-3">
            <h2 className="text-lg font-bold text-slate-900">Atalhos</h2>
            {renderCollapseButton('atalhos')}
          </div>
          {!isBoxCollapsed('atalhos') && (
          <div className={`${fixedCardBodyClass} space-y-3 px-5 pb-4`}>
            {quickLinks.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={item.onClick}
                className="w-full rounded-2xl border border-slate-200 px-4 py-4 text-left transition hover:border-blue-200 hover:bg-blue-50"
              >
                <p className="text-sm font-semibold text-slate-900">{item.label}</p>
                <p className="mt-1 text-sm leading-6 text-slate-600">{item.description}</p>
              </button>
            ))}
          </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className={`${fixedCardClass} border-slate-200 bg-white px-5 py-10 text-center text-sm text-slate-500`}>Carregando fila juridica...</div>
      ) : error ? (
        <div className={`${fixedCardClass} border-slate-200 bg-white px-5 py-10 text-center text-sm text-red-700`}>{error}</div>
      ) : planosJuridico.length === 0 ? (
        <div className={`${fixedCardClass} border-slate-200 bg-white px-5 py-10 text-center text-sm text-slate-500`}>Nenhum plano aguardando parecer juridico neste momento.</div>
      ) : (
        <WorkspaceSectionGroup>
          {planosAguardandoPrimeiraAnalise.length > 0 && (
            <WorkspaceSection
              color="amber"
              title="Aguardando primeira análise"
              count={planosAguardandoPrimeiraAnalise.length}
              actionLabel="Abrir fila completa"
              onAction={() => navigate('/dashboard/gestao/planos-trabalho?etapaAtual=PROCURADORIA_JURIDICA')}
              collapsed={isBoxCollapsed('fila-juridica-primeira')}
              onToggleCollapse={() => toggleBox('fila-juridica-primeira')}
            >
              <div className="overflow-x-auto border-t border-slate-100">
                <table className="w-full text-sm">
                  <thead className="bg-white">
                    <tr className="border-b border-slate-200">
                      <th className="px-5 py-4 text-left font-semibold text-slate-700">Plano</th>
                      <th className="px-4 py-4 text-left font-semibold text-slate-700">Emenda</th>
                      <th className="px-4 py-4 text-left font-semibold text-slate-700">Modalidade</th>
                      <th className="px-4 py-4 text-left font-semibold text-slate-700">Status</th>
                      <th className="px-5 py-4 text-left font-semibold text-slate-700">Atualizacao</th>
                      <th className="px-5 py-4 text-right font-semibold text-slate-700">Acao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {planosAguardandoPrimeiraAnalise.slice(0, 8).map((item) => (
                      <tr key={item.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/80">
                        <td className="px-5 py-4 align-top">
                          <div className="font-semibold text-slate-900">{item.titulo || 'Nao informado'}</div>
                          <div className="text-xs text-slate-500">{item.numeroProtocolo || 'Sem protocolo'}</div>
                        </td>
                        <td className="px-4 py-4 align-top text-slate-700">{item.emendaCodigo || 'Nao informada'}</td>
                        <td className="px-4 py-4 align-top text-slate-700">{modalidadeLabel(item.modalidade)}</td>
                        <td className="px-4 py-4 align-top">
                          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${planoStatusTone(item.status)}`}>
                            {planoStatusLabel(item.status)}
                          </span>
                        </td>
                        <td className="px-5 py-4 align-top text-slate-700">{formatDate(item.dataAtualizacao || item.aprovadoEm || item.enviadoEm)}</td>
                        <td className="px-5 py-4 align-top text-right">
                          <button
                            type="button"
                            onClick={() => openAnaliseJuridica(item.id)}
                            className="inline-flex items-center justify-center rounded-xl bg-blue-700 px-3 py-2 text-xs font-semibold text-white transition hover:bg-blue-800"
                          >
                            Iniciar analise
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </WorkspaceSection>
          )}

          {planosAnaliseIniciada.length > 0 && (
            <WorkspaceSection
              color="indigo"
              title="Análise iniciada"
              count={planosAnaliseIniciada.length}
              actionLabel="Abrir fila completa"
              onAction={() => navigate('/dashboard/gestao/planos-trabalho?etapaAtual=PROCURADORIA_JURIDICA')}
              collapsed={isBoxCollapsed('fila-juridica-iniciada')}
              onToggleCollapse={() => toggleBox('fila-juridica-iniciada')}
            >
              <div className="overflow-x-auto border-t border-slate-100">
                <table className="w-full text-sm">
                  <thead className="bg-white">
                    <tr className="border-b border-slate-200">
                      <th className="px-5 py-4 text-left font-semibold text-slate-700">Plano</th>
                      <th className="px-4 py-4 text-left font-semibold text-slate-700">Emenda</th>
                      <th className="px-4 py-4 text-left font-semibold text-slate-700">Modalidade</th>
                      <th className="px-4 py-4 text-left font-semibold text-slate-700">Status</th>
                      <th className="px-5 py-4 text-left font-semibold text-slate-700">Atualizacao</th>
                      <th className="px-5 py-4 text-right font-semibold text-slate-700">Acao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {planosAnaliseIniciada.slice(0, 8).map((item) => (
                      <tr key={item.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/80">
                        <td className="px-5 py-4 align-top">
                          <div className="font-semibold text-slate-900">{item.titulo || 'Nao informado'}</div>
                          <div className="text-xs text-slate-500">{item.numeroProtocolo || 'Sem protocolo'}</div>
                        </td>
                        <td className="px-4 py-4 align-top text-slate-700">{item.emendaCodigo || 'Nao informada'}</td>
                        <td className="px-4 py-4 align-top text-slate-700">{modalidadeLabel(item.modalidade)}</td>
                        <td className="px-4 py-4 align-top">
                          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${planoStatusTone(item.status)}`}>
                            {planoStatusLabel(item.status)}
                          </span>
                        </td>
                        <td className="px-5 py-4 align-top text-slate-700">{formatDate(item.dataAtualizacao || item.aprovadoEm || item.enviadoEm)}</td>
                        <td className="px-5 py-4 align-top text-right">
                          <button
                            type="button"
                            onClick={() => openAnaliseJuridica(item.id)}
                            className="inline-flex items-center justify-center rounded-xl bg-blue-700 px-3 py-2 text-xs font-semibold text-white transition hover:bg-blue-800"
                          >
                            Continuar analise
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </WorkspaceSection>
          )}
        </WorkspaceSectionGroup>
      )}
    </div>
  );
};

export default ProcuradoriaWorkspace;
